import { useToggle } from "@uidotdev/usehooks";

import { AnimateChangeInHeight } from "@/components/ui/animate-change-height";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { BwCheckmarkLine } from "@/components/ui/icons";
import useTimezone from "@/hooks/use-timezone";
import { ActiveFilterChip } from "../_components/active-filter-chip";
import { FilterCustomDatePicker } from "../_components/filter-custom-date-picker";
import { useFilters } from "../_hooks/use-filters";
import {
  CUSTOM_DATE_LABEL,
  type DatePreset,
  type DateRangeValue,
} from "../date-presets";
import type { FilterField } from "../types";

type DatePresetFilterProps<TItem> = {
  field: FilterField<string, unknown, TItem>;
  presets: DatePreset[];
};

/**
 * Quick-preset date-range chip — the `render` escape hatch on the otherwise
 * generic `dateRange` field, which on its own offers only a custom range. Presets
 * (Today, This week, …) resolve day boundaries in the viewer's timezone and
 * write the same `{ start, end, label }` object the shared `dateRangeCodec`
 * folds into `{ gte, lte }`, so the platform needs no change. "Custom..." opens
 * the shared range picker.
 */
export const DatePresetFilter = <TItem,>({
  field: itemField,
  presets,
}: DatePresetFilterProps<TItem>) => {
  // Only the field's key, label, icon and operators are read here — never its
  // item-typed `accessor` / `matchValue` — so a field over any item type (a
  // list folded in the browser) works.
  const field = itemField as FilterField;
  const { filters, filtersOrder, operatorFor, modify, clear } = useFilters();
  const { timeZone } = useTimezone();
  const key = field.key;
  const value = (filters as Record<string, DateRangeValue | undefined>)[key];
  const selectedLabel = value?.label;

  // A comparison operator reads one bound, so "Custom..." picks a single day.
  // The presets still write both bounds; the codec keeps the one in play.
  const operator = operatorFor(field);
  const isSingleBound = operator !== "range" && operator !== "between";

  // Every preset writes its own name as the label, so a label matching none of
  // them came from the custom picker — that is the value the chip must read as
  // "is between" rather than "is". (Selecting the "Custom..." row opens the
  // picker instead of storing, so its label never reaches the value.)
  const isExplicitRange =
    !!selectedLabel &&
    !presets.some((preset) => preset.label === selectedLabel);

  const [open, toggleOpen] = useToggle(
    !selectedLabel && filtersOrder.at(-1) === key,
  );
  const [openDatePicker, toggleOpenDatePicker] = useToggle(false);

  const onSelectPreset = (preset: DatePreset) => {
    const next = preset.build(timeZone);
    if (next.label === CUSTOM_DATE_LABEL) {
      toggleOpenDatePicker(true);
      return;
    }
    modify({ [key]: next });
    toggleOpen(false);
  };

  return (
    <ActiveFilterChip
      field={field}
      valueLabel={selectedLabel || "..."}
      isExplicitRange={isExplicitRange}
      open={open}
      onOpenChange={toggleOpen}
      onRemove={() => clear(key)}
    >
      <AnimateChangeInHeight>
        <Command
          className="bg-bg-surface-elevate-low w-50"
          shouldFilter={false}
          tabIndex={0}
        >
          <CommandList className="outline-none">
            <CommandGroup>
              {presets.map((preset) => {
                const isSelected =
                  selectedLabel === preset.label ||
                  (!!selectedLabel?.includes("-") &&
                    preset.label === CUSTOM_DATE_LABEL);
                return (
                  <CommandItem
                    key={preset.label}
                    className="group text-text-dark-high flex gap-2 items-center"
                    onSelect={() => onSelectPreset(preset)}
                  >
                    <span className="text-sm leading-5 font-normal text-text-dark-max line-clamp-1 break-all grow">
                      {preset.label}
                    </span>
                    {isSelected && (
                      <BwCheckmarkLine className="size-4 text-text-dark-high" />
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
        <FilterCustomDatePicker
          open={openDatePicker}
          onOpenChange={toggleOpenDatePicker}
          mode={isSingleBound ? "single" : "range"}
          bound={operator === "gt" || operator === "gte" ? "start" : "end"}
          onChange={(next) => modify({ [key]: next })}
          value={value}
        />
      </AnimateChangeInHeight>
    </ActiveFilterChip>
  );
};
